import type {
  Account,
  AccountStatus,
  Connection,
  ConnectionStatus,
  Connector,
} from "@shared/types/connection"
import { httpsCallable } from "firebase/functions"
import { StrictMode, useState } from "react"

import AuthGuard from "./components/AuthGuard"
import { DebugBar } from "./components/DebugBar"
import { LclModal } from "./components/LclModal"
import { functions } from "./firebase"
import { useAccounts } from "./hooks/useAccounts"
import { AuthProvider, useAuth } from "./hooks/useAuth"
import { useConnections } from "./hooks/useConnections"
import { useConnectors } from "./hooks/useConnectors"
import { useLclConnection } from "./hooks/useLclConnection"
import { usePlaidConnection } from "./hooks/usePlaidConnection"
import { useTransactions } from "./hooks/useTransactions"

// ── Status badges ────────────────────────────────────────────────────────────

const CONNECTION_STATUS_STYLES: Record<ConnectionStatus, string> = {
  connected:
    "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
  pending:
    "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400",
  error: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
}

function ConnectionStatusBadge({ status }: { status: ConnectionStatus }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${CONNECTION_STATUS_STYLES[status]}`}
    >
      {status}
    </span>
  )
}

const ACCOUNT_STATUS_STYLES: Record<AccountStatus, string> = {
  active:
    "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
  discovered:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400",
  inactive: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
}

function AccountStatusBadge({ status }: { status: AccountStatus }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${ACCOUNT_STATUS_STYLES[status]}`}
    >
      {status}
    </span>
  )
}

// ── Account row (inside a connection card) ───────────────────────────────────

function AccountRow({
  connection,
  account,
  onRequestSync,
  onDeleteAccount,
  onDeleteTransactions,
  onDeleteTransaction,
}: {
  connection: Connection
  account: Account
  onRequestSync?: (connectionId: string, accountId: string) => Promise<void>
  onDeleteAccount?: (connectionId: string, accountId: string) => Promise<void>
  onDeleteTransactions?: (
    connectionId: string,
    accountId: string,
  ) => Promise<void>
  onDeleteTransaction?: (
    connectionId: string,
    accountId: string,
    transactionId: string,
  ) => Promise<void>
}) {
  const [expanded, setExpanded] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [clearingTx, setClearingTx] = useState(false)
  const [deletingTxId, setDeletingTxId] = useState<string | null>(null)

  const canExpand = account.status === "active"
  const { transactions, loading: txLoading } = useTransactions(
    expanded ? connection.oid : null,
    expanded ? account.oid : null,
  )

  const handleSync = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!onRequestSync) return
    setSyncing(true)
    try {
      await onRequestSync(connection.oid, account.oid)
    } finally {
      setSyncing(false)
    }
  }

  const handleDeleteAccount = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!onDeleteAccount) return
    if (
      !confirm(
        `Deactivate "${account.displayName}"? Transactions will be deleted and the account will return to discovered state.`,
      )
    ) {
      return
    }
    setDeleting(true)
    try {
      await onDeleteAccount(connection.oid, account.oid)
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteTransactions = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!onDeleteTransactions) return
    if (!confirm(`Delete all transactions for "${account.displayName}"?`)) {
      return
    }
    setClearingTx(true)
    try {
      await onDeleteTransactions(connection.oid, account.oid)
    } finally {
      setClearingTx(false)
    }
  }

  const handleDeleteTransaction = async (transactionId: string) => {
    if (!onDeleteTransaction) return
    setDeletingTxId(transactionId)
    try {
      await onDeleteTransaction(connection.oid, account.oid, transactionId)
    } finally {
      setDeletingTxId(null)
    }
  }

  return (
    <div className="border-t border-gray-100 dark:border-gray-700">
      {/* Row header */}
      <div
        className={`flex items-center justify-between px-4 py-2.5 ${canExpand ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40" : ""}`}
        onClick={() => canExpand && setExpanded((v) => !v)}
      >
        <div className="flex min-w-0 items-center gap-2">
          {canExpand && (
            <span className="w-3 shrink-0 text-xs text-gray-400">
              {expanded ? "▾" : "▸"}
            </span>
          )}
          <span className="truncate text-sm">{account.displayName}</span>
          {account.lastBalance !== 0 && (
            <span className="shrink-0 font-mono text-xs text-gray-400">
              {account.lastBalance.toFixed(2)}
            </span>
          )}
        </div>

        <div className="ml-3 flex shrink-0 items-center gap-2">
          <AccountStatusBadge status={account.status} />

          {account.status === "active" && account.syncRequestedAt && (
            <span className="text-xs text-blue-400">Syncing…</span>
          )}

          {account.status === "active" &&
            !account.syncRequestedAt &&
            onRequestSync && (
              <button
                onClick={(e) => void handleSync(e)}
                disabled={syncing || deleting}
                className="rounded bg-blue-600 px-2 py-1 text-xs text-white disabled:opacity-50"
              >
                {syncing ? "…" : "Sync"}
              </button>
            )}

          {onDeleteAccount && (
            <button
              onClick={(e) => void handleDeleteAccount(e)}
              disabled={deleting || syncing}
              className="text-xs text-red-400 hover:text-red-600 disabled:opacity-50"
            >
              {deleting ? "…" : "Deactivate"}
            </button>
          )}
        </div>
      </div>

      {/* Transactions (expanded) */}
      {expanded && (
        <div className="px-4 pb-3 pt-1">
          {txLoading && <p className="text-xs text-gray-400">Loading…</p>}
          {!txLoading && transactions.length === 0 && (
            <p className="text-xs text-gray-400">No transactions yet.</p>
          )}
          {transactions.length > 0 && (
            <>
              {onDeleteTransactions && (
                <div className="mb-1.5 flex justify-end">
                  <button
                    onClick={(e) => void handleDeleteTransactions(e)}
                    disabled={clearingTx || deletingTxId !== null}
                    className="text-xs text-red-400 hover:text-red-600 disabled:opacity-50"
                  >
                    {clearingTx ? "Clearing…" : "Clear all"}
                  </button>
                </div>
              )}
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="text-gray-400">
                    <th className="pb-1 pr-4 font-medium">Date</th>
                    <th className="pb-1 pr-4 text-right font-medium">
                      Montant
                    </th>
                    <th className="pb-1 pr-4 font-medium">Nature</th>
                    <th className="pb-1 font-medium">Libellé</th>
                    {onDeleteTransaction && <th className="pb-1 pl-2" />}
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => (
                    <tr
                      key={t.oid}
                      className={`border-t border-gray-100 dark:border-gray-700/60 ${t.isPending ? "opacity-50" : ""}`}
                    >
                      <td className="py-1 pr-4 text-gray-400">
                        {t.bookingDate}
                      </td>
                      <td
                        className={`py-1 pr-4 text-right font-mono ${t.montant < 0 ? "text-red-500" : "text-green-500"}`}
                      >
                        {t.montant.toFixed(2)}
                      </td>
                      <td className="py-1 pr-4 text-gray-400">{t.nature}</td>
                      <td className="max-w-xs truncate py-1">{t.libelle}</td>
                      {onDeleteTransaction && (
                        <td className="py-1 pl-2">
                          <button
                            onClick={() => void handleDeleteTransaction(t.oid)}
                            disabled={deletingTxId !== null || clearingTx}
                            className="w-4 text-center text-sm leading-none text-gray-400 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-30 dark:text-gray-500 dark:hover:text-red-400"
                          >
                            {deletingTxId === t.oid ? "…" : "×"}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ── Connection card (groups accounts) ────────────────────────────────────────

function ConnectionCard({
  connection,
  connector,
  onDisconnect,
  onRequestSync,
  onDeleteAccount,
  onDeleteTransactions,
  onDeleteTransaction,
}: {
  connection: Connection
  connector?: Connector
  onDisconnect: () => void
  onRequestSync?: (connectionId: string, accountId: string) => Promise<void>
  onDeleteAccount?: (connectionId: string, accountId: string) => Promise<void>
  onDeleteTransactions?: (
    connectionId: string,
    accountId: string,
  ) => Promise<void>
  onDeleteTransaction?: (
    connectionId: string,
    accountId: string,
    transactionId: string,
  ) => Promise<void>
}) {
  const { accounts, loading: accountsLoading } = useAccounts(
    connection.status === "connected" ? connection.oid : null,
  )

  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      {/* Connection header */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2.5">
          {connector?.logoUri && (
            <img
              src={connector.logoUri}
              alt={connector.name}
              className="h-6 w-6 rounded object-contain"
            />
          )}
          <span className="text-sm font-semibold">
            {connection.displayName}
          </span>
          <ConnectionStatusBadge status={connection.status} />
        </div>
        <button
          onClick={onDisconnect}
          className="text-xs text-red-500 hover:text-red-700"
        >
          Disconnect
        </button>
      </div>

      {/* Status details */}
      {connection.status === "pending" && (
        <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-400 dark:border-gray-700">
          Run <code className="font-mono">lcl_discover.py</code> to discover
          accounts
        </p>
      )}
      {connection.status === "error" && (
        <p className="border-t border-gray-100 px-4 py-2 text-xs text-red-400 dark:border-gray-700">
          {connection.errorMessage ?? "Authentication error"}
        </p>
      )}

      {/* Account rows — only show active/inactive, never transient "discovered" */}
      {connection.status === "connected" && (
        <>
          {accountsLoading && (
            <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-400 dark:border-gray-700">
              Loading accounts…
            </p>
          )}
          {!accountsLoading &&
            accounts.filter((a) => a.status !== "discovered").length === 0 && (
              <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-400 dark:border-gray-700">
                No accounts found.
              </p>
            )}
          {accounts
            .filter((a) => a.status !== "discovered")
            .map((account) => (
              <AccountRow
                key={account.oid}
                connection={connection}
                account={account}
                onRequestSync={onRequestSync}
                onDeleteAccount={onDeleteAccount}
                onDeleteTransactions={onDeleteTransactions}
                onDeleteTransaction={onDeleteTransaction}
              />
            ))}
        </>
      )}
    </div>
  )
}

// ── Welcome page ─────────────────────────────────────────────────────────────

const _deleteAccountFn = httpsCallable<
  { connectionId: string; accountId: string },
  void
>(functions, "deleteAccount")

const _deleteTransactionsFn = httpsCallable<
  { connectionId: string; accountId: string },
  void
>(functions, "deleteTransactions")

const _deleteTransactionFn = httpsCallable<
  { connectionId: string; accountId: string; transactionId: string },
  void
>(functions, "deleteTransaction")

const Welcome = () => {
  const { user, error } = useAuth()
  const { connections, disconnect } = useConnections()
  const connectors = useConnectors()
  const {
    connect: connectPlaid,
    loading: plaidLoading,
    error: plaidError,
  } = usePlaidConnection()
  const { requestSync } = useLclConnection()

  const [showLclModal, setShowLclModal] = useState(false)

  const deleteAccount = async (connectionId: string, accountId: string) => {
    await _deleteAccountFn({ connectionId, accountId })
  }

  const deleteTransactions = async (
    connectionId: string,
    accountId: string,
  ) => {
    await _deleteTransactionsFn({ connectionId, accountId })
  }

  const deleteTransaction = async (
    connectionId: string,
    accountId: string,
    transactionId: string,
  ) => {
    await _deleteTransactionFn({ connectionId, accountId, transactionId })
  }

  if (error) console.error(error)

  return (
    <>
      <h1>Gadol</h1>
      <h2>Welcome to {import.meta.env.VITE_FIREBASE_PROJECT_ID}</h2>

      {user?.photoURL && (
        <img
          width={64}
          height={64}
          src={user.photoURL}
          alt="Profile Picture"
          referrerPolicy="no-referrer"
        />
      )}
      <div>
        <b>{user?.displayName}</b>
      </div>
      <div>{user ? user.email : "None"}</div>

      <div className="mt-6 flex max-w-lg flex-col gap-3">
        {/* Connect buttons */}
        <button
          onClick={connectPlaid}
          disabled={plaidLoading}
          className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
        >
          {plaidLoading ? "Connecting…" : "+ Connect Bank"}
        </button>
        {plaidError && (
          <p className="text-sm text-red-500">{plaidError.message}</p>
        )}

        <button
          onClick={() => setShowLclModal(true)}
          className="rounded bg-green-600 px-4 py-2 text-white"
        >
          + Connect with LCL
        </button>

        {/* Connection cards, grouped by connection */}
        {connections.map((c) => (
          <ConnectionCard
            key={c.oid}
            connection={c}
            connector={connectors[c.connectorId]}
            onDisconnect={() => void disconnect(c.oid)}
            onRequestSync={c.connectorId === "lcl" ? requestSync : undefined}
            onDeleteAccount={deleteAccount}
            onDeleteTransactions={deleteTransactions}
            onDeleteTransaction={deleteTransaction}
          />
        ))}
      </div>

      {showLclModal && (
        <LclModal
          onClose={() => setShowLclModal(false)}
          connections={connections}
          existingLclConnection={connections.find(
            (c) => c.connectorId === "lcl",
          )}
        />
      )}
    </>
  )
}

function App() {
  return (
    <StrictMode>
      <AuthProvider>
        <AuthGuard>
          <Welcome />
        </AuthGuard>
        <DebugBar />
      </AuthProvider>
    </StrictMode>
  )
}

export default App
